import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { FileText, Upload, Download, Eye, Trash2, File, Layers, Columns2, FilePlus, FolderPlus, GripVertical } from 'lucide-react';
import AttachmentPreview from '@/components/AttachmentPreview';
import DrawingFolderTree from '@/components/DrawingFolderTree';
import { useAuth } from '@/contexts/AuthContext';
import { canFeature } from '@/lib/perms';
import { useConfirm } from '@/components/ConfirmDialog';

function formatSize(bytes) {
  if (!bytes) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}
function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
async function downloadFile(name, url) {
  if (!url) { toast.error('No file to download'); return; }
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl; a.download = name || 'drawing.pdf';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(objUrl);
  } catch { toast.error(`Download failed: ${name}`); }
}
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
function versionsOf(d) {
  if (Array.isArray(d.versions) && d.versions.length) return [...d.versions].sort((a, b) => (b.version || 1) - (a.version || 1));
  return [{ version: 1, name: d.name, size: d.size, data_url: d.data_url, type: d.type, created_at: d.created_at }];
}

const CLIENT_ACCESS_OPTIONS = [
  { value: 'none', label: 'Internal only (hidden from clients)' },
  { value: 'view', label: 'Client can view' },
  { value: 'download', label: 'Client can view + download' },
];
const TEAM_ACCESS_OPTIONS = [
  { value: 'edit', label: 'Team can upload & delete' },
  { value: 'view', label: 'Team view only (owner edits)' },
];

export default function ProjectDrawings({ projectId }) {
  const { user } = useAuth();
  const [confirm, confirmDialog] = useConfirm();
  const canManageFolders = canFeature(user, 'manageData'); // folder add/edit/delete is admin-only
  const [drawings, setDrawings] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState('__all__');
  const [viewFile, setViewFile] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [historyDrawing, setHistoryDrawing] = useState(null);
  const [compare, setCompare] = useState(null);
  const [folderDialog, setFolderDialog] = useState(null); // {mode:'add'|'edit', parent_id?, folder?}
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const versionInputRef = useRef(null);
  const versionTargetRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [dRes, fRes] = await Promise.all([
        api.get(`/projects/${projectId}/drawings`),
        api.get(`/projects/${projectId}/drawing-folders`),
      ]);
      setDrawings(dRes.data || []);
      setFolders(fRes.data || []);
    } catch (err) { console.error('Failed to load drawings:', err); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  // Counts per folder
  const counts = {};
  for (const d of drawings) {
    const fid = d.folder_id || '';
    if (fid) counts[fid] = (counts[fid] || 0) + 1;
  }

  // Default landing folder: lowest-ordered root folder. Every file lives here-or-deeper.
  const firstFolderId = (() => {
    const roots = folders.filter(f => !f.parent_id);
    const ordered = [...(roots.length ? roots : folders)].sort((a, b) => (a.order || 0) - (b.order || 0));
    return ordered[0]?.id || '';
  })();

  const currentFolder = folders.find(f => f.id === selected) || null;
  const visible = drawings.filter(d => {
    if (selected === '__all__') return true;
    return d.folder_id === selected;
  });

  const uploadFiles = async (files, folderId) => {
    const pdfs = Array.from(files).filter(f => f.type === 'application/pdf');
    if (pdfs.length === 0) { toast.error('Only PDF files are accepted'); return; }
    // Files must land in a real folder: the current one, or the first folder from "All Drawings".
    const targetFolder = folderId ?? (selected !== '__all__' ? selected : firstFolderId);
    if (!targetFolder) { toast.error('Create a folder first'); return; }
    setUploading(true);
    for (const file of pdfs) {
      try {
        const dataUrl = await readFileAsDataUrl(file);
        await api.post(`/projects/${projectId}/drawings`, { name: file.name, size: file.size, data_url: dataUrl, folder_id: targetFolder });
        toast.success(`Uploaded: ${file.name}`);
      } catch { toast.error(`Failed to upload: ${file.name}`); }
    }
    setUploading(false);
    load();
  };

  const uploadNewVersion = async (drawingId, files) => {
    const file = Array.from(files).find(f => f.type === 'application/pdf');
    if (!file) { toast.error('Only PDF files are accepted'); return; }
    setUploading(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const res = await api.post(`/projects/${projectId}/drawings/${drawingId}/versions`, { name: file.name, size: file.size, data_url: dataUrl });
      toast.success(`New version uploaded (V${res.data.version})`);
      await load();
    } catch { toast.error('Failed to upload new version'); }
    finally { setUploading(false); }
  };

  const moveDrawing = async (drawingId, folderId) => {
    if (!folderId || folderId === '__all__') return;
    try {
      await api.put(`/projects/${projectId}/drawings/${drawingId}/move`, { folder_id: folderId });
      const fname = folders.find(f => f.id === folderId)?.name || 'folder';
      toast.success(`Moved to ${fname}`);
      await load();
    } catch { toast.error('Move failed'); }
  };

  const deleteDrawing = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/projects/${projectId}/drawings/${deleteTarget.id}`);
      toast.success('Drawing deleted');
      await load();
    } catch { toast.error('Delete failed'); }
    setDeleteTarget(null);
  };

  const deleteVersion = async (drawingId, version) => {
    if (!(await confirm({ title: `Delete version V${version}?`, message: 'This version and its file will be permanently deleted. This cannot be undone.', confirmLabel: 'Delete' }))) return;
    try { await api.delete(`/projects/${projectId}/drawings/${drawingId}/versions/${version}`); toast.success(`V${version} deleted`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const downloadAll = async (d) => {
    const vs = versionsOf(d);
    toast.message(`Downloading ${vs.length} version${vs.length > 1 ? 's' : ''}…`);
    for (const v of vs) {
      await downloadFile(`${(d.name || 'drawing').replace(/\.pdf$/i, '')}_V${v.version}.pdf`, v.data_url);
      await new Promise(r => setTimeout(r, 400));
    }
  };

  const saveFolder = async (payload) => {
    try {
      if (folderDialog.mode === 'edit') {
        await api.put(`/projects/${projectId}/drawing-folders/${folderDialog.folder.id}`, payload);
        toast.success('Folder updated');
      } else {
        await api.post(`/projects/${projectId}/drawing-folders`, { ...payload, parent_id: folderDialog.parent_id || null });
        toast.success('Folder created');
      }
      setFolderDialog(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save folder'); }
  };

  const deleteFolder = async () => {
    const f = folderDialog.folder;
    if (!(await confirm({ title: 'Delete folder?', message: `"${f.name}" and its subfolders will be deleted. Drawings inside move to the first folder — the files themselves are kept.`, confirmLabel: 'Delete' }))) return;
    try {
      await api.delete(`/projects/${projectId}/drawing-folders/${f.id}`);
      toast.success('Folder deleted');
      if (selected === f.id) setSelected('__all__');
      setFolderDialog(null);
      await load();
    } catch { toast.error('Delete failed'); }
  };

  const triggerVersionUpload = (drawingId) => { versionTargetRef.current = drawingId; versionInputRef.current?.click(); };
  const liveHistory = historyDrawing ? drawings.find(d => d.id === historyDrawing.id) : null;

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="flex flex-col md:flex-row gap-4" data-testid="project-drawings">
      {/* Left: folder tree */}
      <div className="md:w-64 shrink-0">
        <div className="glass-card rounded-xl p-3">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Folders</span>
            {canManageFolders && (
              <button onClick={() => setFolderDialog({ mode: 'add', parent_id: null })} title="Add folder"
                className="p-1 rounded-lg hover:bg-[#3A8C8C]/10 text-[#3A8C8C]" data-testid="add-root-folder"><FolderPlus size={15} /></button>
            )}
          </div>
          <DrawingFolderTree
            folders={folders} counts={counts} selectedId={selected}
            onSelect={setSelected}
            canManage={canManageFolders}
            onAddSub={(parentId) => setFolderDialog({ mode: 'add', parent_id: parentId })}
            onManage={(folder) => setFolderDialog({ mode: 'edit', folder })}
            onDropDrawing={moveDrawing}
          />
        </div>
      </div>

      {/* Right: upload + file list */}
      <div className="flex-1 min-w-0 space-y-4">
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all ${dragOver ? 'border-[#3A8C8C] bg-[#3A8C8C]/5' : 'border-[#D4D0C8] hover:border-[#3A8C8C]/50 hover:bg-[#FAFAF8]'}`}
          data-testid="drawings-upload-zone"
        >
          <input ref={fileRef} type="file" accept="application/pdf" multiple className="hidden" onChange={e => { uploadFiles(e.target.files); e.target.value = ''; }} />
          <Upload size={24} strokeWidth={1.5} className={`mx-auto mb-2 ${dragOver ? 'text-[#3A8C8C]' : 'text-[#D4D0C8]'}`} />
          <p className="text-sm font-medium text-[#2C2C2E]">
            {uploading ? 'Uploading...' : `Drop PDFs into ${currentFolder ? `"${currentFolder.name}"` : (folders.find(f => f.id === firstFolderId)?.name || 'the first folder')}`}
          </p>
          <p className="text-xs text-[#6B7280] mt-1">or click to browse · revised uploads become new versions</p>
        </div>

        <input ref={versionInputRef} type="file" accept="application/pdf" className="hidden"
          onChange={e => { const id = versionTargetRef.current; if (id) uploadNewVersion(id, e.target.files); e.target.value = ''; }} />

        {visible.length > 0 ? (
          <div className="space-y-2" data-testid="drawings-file-list">
            {visible.map(d => {
              const vs = versionsOf(d);
              const ver = d.version || vs[0].version || 1;
              const latest = vs[0];
              const folder = folders.find(f => f.id === d.folder_id);
              return (
                <div key={d.id} draggable onDragStart={e => e.dataTransfer.setData('drawing-id', d.id)}
                  className="glass-card rounded-xl px-3 py-3 flex items-center gap-2 group" data-testid={`drawing-row-${d.id}`}>
                  <GripVertical size={14} className="text-[#D4D0C8] cursor-grab shrink-0" />
                  <div className="w-9 h-9 rounded-lg bg-[#C4727F]/10 flex items-center justify-center shrink-0"><FileText size={18} strokeWidth={1.5} className="text-[#C4727F]" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-[#2C2C2E] truncate">{d.name}</p>
                      <Badge className="bg-[#3A8C8C]/10 text-[#3A8C8C] text-[9px] px-1.5 py-0 shrink-0" data-testid={`drawing-version-badge-${d.id}`}>V{ver}</Badge>
                    </div>
                    <p className="text-[10px] text-[#6B7280]">
                      {formatSize(latest.size)}{vs.length > 1 ? ` · ${vs.length} versions` : ''}
                      {selected === '__all__' && folder ? ` · ${folder.name}` : ''}
                      {latest.created_at ? ` · ${formatDate(latest.created_at)}` : ''}
                    </p>
                  </div>
                  {/* Move dropdown */}
                  <select value={d.folder_id || firstFolderId} onChange={e => moveDrawing(d.id, e.target.value)} onClick={e => e.stopPropagation()}
                    title="Move to folder" data-testid={`drawing-move-${d.id}`}
                    className="opacity-0 group-hover:opacity-100 h-7 max-w-[110px] rounded-lg border border-[#D4D0C8] text-[10px] px-1 bg-white outline-none focus:border-[#3A8C8C] transition-opacity">
                    {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button onClick={() => setViewFile({ name: d.name, data_url: latest.data_url, type: latest.type || 'application/pdf', size: latest.size })} className="p-1.5 rounded-lg hover:bg-[#3A8C8C]/10" title="View latest" data-testid={`drawing-view-${d.id}`}><Eye size={15} className="text-[#3A8C8C]" /></button>
                    <button onClick={() => triggerVersionUpload(d.id)} className="p-1.5 rounded-lg hover:bg-[#3A8C8C]/10" title="Upload new version" data-testid={`drawing-newversion-${d.id}`}><FilePlus size={15} className="text-[#3A8C8C]" /></button>
                    <button onClick={() => setHistoryDrawing(d)} className="p-1.5 rounded-lg hover:bg-[#3A8C8C]/10 relative" title="Version history" data-testid={`drawing-history-${d.id}`}>
                      <Layers size={15} className="text-[#3A8C8C]" />
                      {vs.length > 1 && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#3A8C8C] text-white text-[8px] flex items-center justify-center">{vs.length}</span>}
                    </button>
                    {vs.length > 1 && <button onClick={() => setCompare({ drawing: d, aVer: vs[1]?.version ?? vs[0].version, bVer: vs[0].version })} className="p-1.5 rounded-lg hover:bg-[#3A8C8C]/10" title="Compare versions" data-testid={`drawing-compare-${d.id}`}><Columns2 size={15} className="text-[#3A8C8C]" /></button>}
                    <button onClick={() => downloadFile(d.name, latest.data_url)} className="p-1.5 rounded-lg hover:bg-[#3A8C8C]/10" title="Download latest" data-testid={`drawing-download-${d.id}`}><Download size={15} className="text-[#3A8C8C]" /></button>
                    <button onClick={() => setDeleteTarget(d)} className="p-1.5 rounded-lg hover:bg-[#C4727F]/10" title="Delete" data-testid={`drawing-delete-${d.id}`}><Trash2 size={15} className="text-[#C4727F]" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (!uploading && (
          <div className="text-center py-10">
            <File size={36} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-3" />
            <p className="text-sm text-[#6B7280]">No drawings in {currentFolder ? `"${currentFolder.name}"` : 'this view'} yet.</p>
          </div>
        ))}
      </div>

      <AttachmentPreview attachment={viewFile} onClose={() => setViewFile(null)} />

      {/* Folder add/edit dialog */}
      <FolderDialog dialog={folderDialog} folders={folders} onClose={() => setFolderDialog(null)} onSave={saveFolder} onDelete={deleteFolder} />

      {/* Version history */}
      <Dialog open={!!historyDrawing} onOpenChange={() => setHistoryDrawing(null)}>
        <DialogContent className="max-w-lg" data-testid="drawing-history-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope'] text-base flex items-center gap-2"><Layers size={16} className="text-[#3A8C8C]" /> Version History</DialogTitle></DialogHeader>
          {liveHistory && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-[#6B7280] truncate">{liveHistory.name}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => triggerVersionUpload(liveHistory.id)} className="flex items-center gap-1 text-xs text-[#3A8C8C] font-medium hover:underline" data-testid="history-new-version"><FilePlus size={12} /> New Version</button>
                  <button onClick={() => downloadAll(liveHistory)} className="flex items-center gap-1 text-xs text-[#3A8C8C] font-medium hover:underline" data-testid="history-download-all"><Download size={12} /> Download All</button>
                </div>
              </div>
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {versionsOf(liveHistory).map((v, i) => (
                  <div key={v.version} className={`flex items-center gap-3 p-2.5 rounded-lg border ${i === 0 ? 'border-[#3A8C8C]/30 bg-[#3A8C8C]/5' : 'border-[#E8E6E1]'}`} data-testid={`history-version-${v.version}`}>
                    <Badge className={`text-[9px] px-1.5 py-0 shrink-0 ${i === 0 ? 'bg-[#3A8C8C] text-white' : 'bg-[#E8E6E1] text-[#6B7280]'}`}>V{v.version}</Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#2C2C2E] truncate">{v.name || liveHistory.name}{i === 0 ? '  · current' : ''}</p>
                      <p className="text-[10px] text-[#6B7280]">{formatSize(v.size)}{v.uploaded_by_name ? ` · ${v.uploaded_by_name}` : ''}{v.created_at ? ` · ${formatDate(v.created_at)}` : ''}</p>
                    </div>
                    <button onClick={() => setViewFile({ name: `${liveHistory.name} (V${v.version})`, data_url: v.data_url, type: v.type || 'application/pdf', size: v.size })} className="p-1.5 rounded hover:bg-[#3A8C8C]/10 text-[#3A8C8C]" title="View"><Eye size={13} /></button>
                    <button onClick={() => downloadFile(`${(liveHistory.name || 'drawing').replace(/\.pdf$/i, '')}_V${v.version}.pdf`, v.data_url)} className="p-1.5 rounded hover:bg-[#3A8C8C]/10 text-[#3A8C8C]" title="Download"><Download size={13} /></button>
                    {versionsOf(liveHistory).length > 1 && <button onClick={() => deleteVersion(liveHistory.id, v.version)} className="p-1.5 rounded hover:bg-[#C4727F]/10 text-[#C4727F]" title="Delete version"><Trash2 size={13} /></button>}
                  </div>
                ))}
              </div>
              {versionsOf(liveHistory).length > 1 && (
                <button onClick={() => { const vs = versionsOf(liveHistory); setCompare({ drawing: liveHistory, aVer: vs[1].version, bVer: vs[0].version }); setHistoryDrawing(null); }} className="w-full flex items-center justify-center gap-1.5 h-9 rounded-lg bg-[#3A8C8C]/10 text-[#3A8C8C] text-xs font-medium hover:bg-[#3A8C8C]/15" data-testid="history-compare"><Columns2 size={13} /> Compare Versions</button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Compare */}
      <Dialog open={!!compare} onOpenChange={() => setCompare(null)}>
        <DialogContent className="max-w-6xl w-[92vw]" data-testid="drawing-compare-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope'] text-base flex items-center gap-2"><Columns2 size={16} className="text-[#3A8C8C]" /> Compare Versions — {compare?.drawing?.name}</DialogTitle></DialogHeader>
          {compare && (() => {
            const vs = versionsOf(compare.drawing);
            const pick = (n) => vs.find(v => v.version === n) || vs[0];
            const pane = (side) => {
              const sel = pick(side === 'a' ? compare.aVer : compare.bVer);
              return (
                <div className="flex-1 min-w-0 flex flex-col border border-[#E8E6E1] rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between gap-2 px-3 py-2 bg-[#FAFAF8] border-b border-[#E8E6E1]">
                    <select value={side === 'a' ? compare.aVer : compare.bVer} onChange={e => setCompare(c => ({ ...c, [side === 'a' ? 'aVer' : 'bVer']: Number(e.target.value) }))} className="h-8 rounded-lg border border-[#D4D0C8] text-xs px-2 bg-white outline-none focus:border-[#3A8C8C]" data-testid={`compare-select-${side}`}>
                      {vs.map(v => <option key={v.version} value={v.version}>V{v.version}{v.created_at ? ` · ${formatDate(v.created_at)}` : ''}</option>)}
                    </select>
                    <button onClick={() => downloadFile(`${(compare.drawing.name || 'drawing').replace(/\.pdf$/i, '')}_V${sel.version}.pdf`, sel.data_url)} className="flex items-center gap-1 text-xs text-[#3A8C8C] hover:underline"><Download size={12} /> Download</button>
                  </div>
                  <iframe src={sel.data_url} title={`V${sel.version}`} className="w-full h-[65vh] bg-white" data-testid={`compare-frame-${side}`} />
                </div>
              );
            };
            return <div className="flex gap-3">{pane('a')}{pane('b')}</div>;
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete drawing */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-['Manrope'] text-base">Delete Drawing?</DialogTitle></DialogHeader>
          <p className="text-xs text-[#6B7280] mt-1"><span className="font-medium text-[#2C2C2E]">{deleteTarget?.name}</span> and all its versions will be permanently deleted.</p>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setDeleteTarget(null)} className="flex-1 h-9 rounded-lg border border-[#D4D0C8] text-xs font-medium text-[#6B7280] hover:bg-[#E8E6E1]" data-testid="drawing-delete-cancel">Cancel</button>
            <button onClick={deleteDrawing} className="flex-1 h-9 rounded-lg bg-[#C4727F] text-white text-xs font-medium hover:bg-[#B5616E]" data-testid="drawing-delete-confirm">Delete</button>
          </div>
        </DialogContent>
      </Dialog>

      {confirmDialog}
    </div>
  );
}

function FolderDialog({ dialog, folders, onClose, onSave, onDelete }) {
  const [name, setName] = useState('');
  const [clientAccess, setClientAccess] = useState('view');
  const [teamAccess, setTeamAccess] = useState('edit');

  useEffect(() => {
    if (!dialog) return;
    if (dialog.mode === 'edit') {
      setName(dialog.folder.name || '');
      setClientAccess(dialog.folder.client_access || 'view');
      setTeamAccess(dialog.folder.team_access || 'edit');
    } else {
      setName(''); setClientAccess('view'); setTeamAccess('edit');
    }
  }, [dialog]);

  if (!dialog) return null;
  const parentName = dialog.parent_id ? folders.find(f => f.id === dialog.parent_id)?.name : null;
  const submit = () => {
    if (!name.trim()) { toast.error('Folder name is required'); return; }
    onSave({ name: name.trim(), client_access: clientAccess, team_access: teamAccess });
  };

  return (
    <Dialog open={!!dialog} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="folder-dialog">
        <DialogHeader><DialogTitle className="font-['Manrope'] text-base">
          {dialog.mode === 'edit' ? 'Folder Settings' : parentName ? `New Subfolder in "${parentName}"` : 'New Folder'}
        </DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] text-[#6B7280] uppercase">Name</Label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Structural" autoFocus
              className="mt-1 h-9 w-full rounded-lg border border-[#D4D0C8] text-sm px-2 bg-white outline-none focus:border-[#3A8C8C]" data-testid="folder-name-input" />
          </div>
          <div>
            <Label className="text-[10px] text-[#6B7280] uppercase">Client Access</Label>
            <select value={clientAccess} onChange={e => setClientAccess(e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border border-[#D4D0C8] text-sm px-2 bg-white outline-none focus:border-[#3A8C8C]" data-testid="folder-client-access">
              {CLIENT_ACCESS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <p className="text-[10px] text-[#6B7280] mt-1">
              {clientAccess === 'none' ? 'Hidden from client & vendor portals (e.g. CAD).' : 'Shown in the client portal.'}
            </p>
          </div>
          <div>
            <Label className="text-[10px] text-[#6B7280] uppercase">Team Access</Label>
            <select value={teamAccess} onChange={e => setTeamAccess(e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border border-[#D4D0C8] text-sm px-2 bg-white outline-none focus:border-[#3A8C8C]" data-testid="folder-team-access">
              {TEAM_ACCESS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex gap-2 pt-1">
            {dialog.mode === 'edit' && !dialog.folder.is_default && (
              <button onClick={onDelete} className="px-3 h-9 rounded-lg border border-[#C4727F]/40 text-[#C4727F] text-xs font-medium hover:bg-[#C4727F]/10" data-testid="folder-delete">Delete</button>
            )}
            <div className="flex-1" />
            <button onClick={onClose} className="px-3 h-9 rounded-lg text-xs text-[#6B7280] hover:bg-[#E8E6E1]">Cancel</button>
            <button onClick={submit} className="px-4 h-9 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A]" data-testid="folder-save">{dialog.mode === 'edit' ? 'Save' : 'Create'}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
